import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { Garage, GarageFollower } from '../api/types';

interface Props {
  /** The garage whose followers are managed; null hides the dialog. */
  garage: Garage | null;
  onClose: () => void;
  /** Called after an answer so the page can refresh its follower count. */
  onAnswered?: () => void;
}

/**
 * Owner's view of who follows a garage: approve or refuse the people asking,
 * and remove someone who already follows.
 */
export function GarageFollowersDialog({ garage, onClose, onAnswered }: Props) {
  const { t } = useTranslation();
  const [followers, setFollowers] = useState<GarageFollower[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!garage) return;
    setLoading(true);
    setError('');
    apiFetch<{ data: GarageFollower[] }>(`/api/v1/garages/${garage.id}/followers`)
      .then((res) => setFollowers(res.data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [garage]);

  const answer = async (follower: GarageFollower, decision: 'approve' | 'deny') => {
    if (!garage) return;
    setBusyUserId(follower.user_id);
    setError('');
    try {
      const res = await apiFetch<{ data: GarageFollower }>(
        `/api/v1/garages/${garage.id}/followers/${follower.user_id}/${decision}`,
        { method: 'POST' }
      );
      setFollowers((prev) => prev.map((f) => (f.id === res.data.id ? res.data : f)));
      onAnswered?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('garage.followers.failed'));
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <Dialog open={garage !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('garage.followers.title', { garage: garage?.name ?? '' })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : followers.length === 0 ? (
            <Typography color="text.secondary">{t('garage.followers.empty')}</Typography>
          ) : (
            <List disablePadding>
              {followers.map((follower) => (
                <ListItem key={follower.id} disableGutters>
                  <ListItemAvatar>
                    <Avatar src={follower.user?.avatar_url ?? undefined}>
                      {follower.user?.name?.[0] ?? '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={follower.user?.name ?? '—'}
                    secondary={t(`garage.followers.status.${follower.status}`)}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {follower.status === 'approved' && (
                      <Chip size="small" color="success" label={t('garage.followers.badge')} />
                    )}
                    {follower.status !== 'approved' && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busyUserId === follower.user_id}
                        onClick={() => answer(follower, 'approve')}
                      >
                        {t('garage.followers.approve')}
                      </Button>
                    )}
                    {follower.status !== 'denied' && (
                      <Button
                        size="small"
                        color="error"
                        disabled={busyUserId === follower.user_id}
                        onClick={() => answer(follower, 'deny')}
                      >
                        {follower.status === 'approved'
                          ? t('garage.followers.remove')
                          : t('garage.followers.deny')}
                      </Button>
                    )}
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
